import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Building2,
  Flag,
  Trophy,
  TrendingUp,
  Clock,
} from "lucide-react";

export default function AdminDashboard() {
  const { adminUser } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${API}/admin/dashboard`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
          },
        });
        if (res.ok) {
          setData(await res.json());
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const metrics = data?.metrics || {};
  const recentUsers = data?.recent_users || [];

  const METRIC_CARDS = [
    { label: "Usuarios Totales", value: metrics.total_users || 0, icon: Users, color: "bg-blue-100 text-blue-600" },
    { label: "Clubes", value: metrics.total_clubs || 0, icon: Building2, color: "bg-emerald-100 text-emerald-600" },
    { label: "Administradores", value: metrics.total_admins || 0, icon: Trophy, color: "bg-amber-100 text-amber-600" },
    { label: "Rondas Totales", value: metrics.total_rounds || 0, icon: Flag, color: "bg-purple-100 text-purple-600" },
    { label: "Jugadores", value: metrics.total_players || 0, icon: TrendingUp, color: "bg-rose-100 text-rose-600" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A]" data-testid="admin-dashboard-title">
          Hola, {adminUser?.name?.split(" ")[0] || "Admin"}
        </h1>
        <p className="text-[#64748B] mt-1">Panel de control de cadii</p>
      </div>

      {/* Metrics */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-metrics-grid">
            {METRIC_CARDS.map((m) => {
              const Icon = m.icon;
              return (
                <Card key={m.label} className="card-default">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.color}`}>
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#0F172A]">{m.value}</p>
                        <p className="text-xs text-[#64748B]">{m.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Recent Users */}
          <div>
            <h2 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#64748B]" strokeWidth={1.5} />
              Usuarios Recientes
            </h2>
            <Card className="card-default">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-recent-users-table">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Nombre</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Rol</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Creado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentUsers.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-[#64748B]">
                            No hay usuarios registrados
                          </td>
                        </tr>
                      ) : (
                        recentUsers.map((u) => (
                          <tr key={u.user_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="px-4 py-3 font-medium text-[#0F172A]">{u.name}</td>
                            <td className="px-4 py-3 text-[#64748B]">{u.email}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`text-xs px-2 py-1 rounded-full font-medium ${
                                  u.role === "admin_global"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-blue-100 text-blue-700"
                                }`}
                              >
                                {u.role === "admin_global" ? "Admin" : "Jugador"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[#64748B]">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
